import { useCallback, useRef, useState } from 'react'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { ExtractResponse } from './types'

export type ExtractionQueueStatus = 'queued' | 'extracting' | 'extracted' | 'error'

export interface ExtractionQueueItem {
  /** Stable client-side id so React keys survive list re-renders. */
  clientId: string
  file: File
  status: ExtractionQueueStatus
  /** Set on `extracted` — the raw `extracted` payload from the shipment-sheet
   *  vision extraction. Module-side (`NewSubmissionDrawer`) parses this into
   *  its own `Extracted` shape. */
  extracted?: Record<string, unknown>
  /** User-facing error message on `error` status. */
  errorMessage?: string
}

/**
 * Extraction queue with 3-wide concurrency. Mirrors
 * `useSalesIntakeUploadQueue` / `useCofaUploadQueue`, but the per-item
 * worker is a synchronous extract call (`POST /extract-shipment` returns
 * the parsed JSON directly) rather than an upload-then-poll job — there's
 * no server-side Celery job to observe afterward.
 *
 * Why 3-wide: same answer as the other two queues — lets the user drop a
 * stack of shipment sheets without waiting on a sequential queue, while
 * capping concurrent Claude-vision extraction calls against one endpoint.
 */
const CONCURRENCY = 3
const BASE = '/api/v1/uviva'

let _id = 0
function nextId() {
  _id += 1
  return `extract-${_id}-${Date.now()}`
}

export interface UseUvivaShipmentExtractionQueueOpts {
  /** Called once per successful extraction. Host builds a review entry. */
  onItemExtracted?: (item: ExtractionQueueItem) => void
  /** Called once when the queue drains — every item has reached `extracted`
   *  or `error` and nothing is in flight. Fired from the worker's `finally`
   *  (not a `useEffect`), so the host can call `setPhase(...)` directly. */
  onSettled?: () => void
}

export function useUvivaShipmentExtractionQueue(
  opts: UseUvivaShipmentExtractionQueueOpts = {},
) {
  const [items, setItems] = useState<ExtractionQueueItem[]>([])
  // Mirrors `items` synchronously (React state updates are batched/async),
  // so `processOne`'s `finally` can check "is anything still queued or
  // extracting?" against ground truth the instant its own update lands.
  const itemsRef = useRef<ExtractionQueueItem[]>([])
  const inFlightRef = useRef(0)
  const onItemExtracted = opts.onItemExtracted
  const onSettled = opts.onSettled

  const syncItems = useCallback((next: ExtractionQueueItem[]) => {
    itemsRef.current = next
    setItems(next)
  }, [])

  const updateItem = useCallback(
    (clientId: string, patch: Partial<ExtractionQueueItem>) => {
      syncItems(itemsRef.current.map((it) => (it.clientId === clientId ? { ...it, ...patch } : it)))
    },
    [syncItems],
  )

  const pumpRef = useRef<() => void>(() => {})

  const processOne = useCallback(
    async (item: ExtractionQueueItem) => {
      inFlightRef.current += 1
      updateItem(item.clientId, { status: 'extracting' })
      try {
        const fd = new FormData()
        fd.append('shipment_sheet', item.file)
        const result = await jsonFetch<ExtractResponse>(`${BASE}/extract-shipment`, {
          method: 'POST',
          rawBody: fd,
        })
        const next: Partial<ExtractionQueueItem> = {
          status: 'extracted',
          extracted: result.extracted,
        }
        updateItem(item.clientId, next)
        onItemExtracted?.({ ...item, ...next })
      } catch (err) {
        updateItem(item.clientId, {
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'Extraction failed',
        })
      } finally {
        inFlightRef.current -= 1
        const stillActive = itemsRef.current.some(
          (it) => it.status === 'queued' || it.status === 'extracting',
        )
        if (!stillActive && inFlightRef.current === 0) onSettled?.()
        // Re-pump so the next queued item picks up the freed slot.
        pumpRef.current()
      }
    },
    [onItemExtracted, onSettled, updateItem],
  )

  /** Pull `queued` items off the front of the queue up to CONCURRENCY. */
  const pump = useCallback(() => {
    const queued = itemsRef.current.filter((it) => it.status === 'queued')
    const slots = Math.max(0, CONCURRENCY - inFlightRef.current)
    const next = queued.slice(0, slots)
    for (const item of next) {
      void processOne(item)
    }
  }, [processOne])

  // Keep pumpRef pointed at the current pump so processOne's finally
  // block re-pumps with fresh state.
  // eslint-disable-next-line react-hooks/refs -- intentional always-current ref pattern; processOne reads through pumpRef.current in its finally, where the latest pump (capturing fresh queue state) must be invoked.
  pumpRef.current = pump

  /** Add files to the queue + start pumping. */
  const enqueue = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files)
      if (list.length === 0) return
      syncItems([
        ...itemsRef.current,
        ...list.map(
          (file): ExtractionQueueItem => ({
            clientId: nextId(),
            file,
            status: 'queued',
          }),
        ),
      ])
      // Pump on next tick so the new items render before the dispatch.
      queueMicrotask(pump)
    },
    [pump, syncItems],
  )

  /** Remove a single item by clientId. Doesn't abort an in-flight network
   *  call — just hides the row. */
  const removeItem = useCallback((clientId: string) => {
    syncItems(itemsRef.current.filter((it) => it.clientId !== clientId))
  }, [syncItems])

  /** Wipe the queue entirely — used when the drawer resets. */
  const reset = useCallback(() => {
    syncItems([])
  }, [syncItems])

  const busy = items.some(
    (it) => it.status === 'queued' || it.status === 'extracting',
  )

  return {
    items,
    enqueue,
    removeItem,
    reset,
    busy,
  }
}
